import { Breakpoint, useMediaQuery, useTheme } from "@mui/material";
import config from "../config";
import { t } from "@lingui/macro";
import { getAccessToken } from "./authPersistence";
import { getActiveHomeId } from "../store/instance";

// Home/ad mutation endpoints scope to a property server-side via
// resolveHomeMiddleware. The client tags each with the active property id so
// the right home is edited once a user owns several (multi-home).
const HOME_SCOPED_PATHS = new Set([
  "/api/homeChangeContacts",
  "/api/homeChangeInfo",
  "/api/homeChangeProfileImage",
  "/api/homeDeleteTenant",
  "/api/homeChangeTenants",
  "/api/homeChangeBedrooms",
  "/api/homeChangeBathrooms",
  "/api/homeChangeOtherRooms",
  "/api/homeChangeServices",
  "/api/homeChangePreferences",
  "/api/homeChangeDescription",
  "/api/homeChangeSquare",
  "/api/homeAddPhoto",
  "/api/homeDeletePhoto",
  "/api/saveAdvertise",
  "/api/deleteAdvertise",
]);

export const scrollFieldIntoView = (event: Event) => {
  (event.target as HTMLElement).scrollIntoView(true);
};

export function toggleNotLoggedModal(text: string) {
  const button = document.getElementById(
    "NotLoggedModalButton"
  ) as HTMLButtonElement;
  if (button) {
    button.value = text;
    button.click();
  }
}

export function toggleNotVerifiedModal(text: string, cb: () => void) {
  const button = document.getElementById(
    "NotVerifiedModalButton"
  ) as HTMLButtonElement;
  if (button) {
    (button.value as any) = { text, cb };
    button.click();
  }
}

export function loginClick() {
  setTimeout(() => document.getElementById("loginNavbarButton")?.click(), 100);
}

// Pricing path carrying the current page as returnTo, so checkout can send
// the user back to the profile/ad they were trying to contact.
export function pricingPathWithReturn(): string {
  const returnTo = window.location.pathname + window.location.search;
  return "/pricing?returnTo=" + encodeURIComponent(returnTo);
}

export const userCompleteness = (user: any) => {
  let percentage = 0;
  if (!user.logged) return 0;
  if (user.seeking) {
    if (user.budget > 0) percentage += 8;
    if (user.room_types.length > 0) percentage += 8;
  } else {
    percentage += 16;
  }

  if (user.location.length > 3) percentage += 5;
  if (user.type !== "N") percentage += 5;

  if (user.isNotifMailConfirmed) percentage += 5;

  if (user.interests.length > 0) percentage += 10;
  if (user.lifestyles.length > 0) percentage += 10;

  //if (user.sport > -1) percentage += 5;
  if (user.weekend > -1) percentage += 5;
  if (user.alcol > -1) percentage += 5;
  if (user.smoke > -1) percentage += 5;
  if (user.animals > -1) percentage += 5;
  if (user.relationship > -1) percentage += 5;
  if (user.education > -1) percentage += 5;
  if (user.videoId && user.videoId.length > 0) percentage += 15;
  if (user.description.length >= 30) percentage += 10;
  if (!user.profile_image.includes("default")) percentage += 10;

  return Math.min(percentage, 100);
};

export const userMissingElements = (user: any) => {
  let missing: string[] = [];

  if (user.seeking && !isPartner()) {
    if (user.budget === 0) missing.push(t`Budget`);
    if (user.room_types.length === 0) missing.push(t`Tipo di stanza ricercata`);
  }

  if (user.location.length <= 3) missing.push(t`Provenienza`);
  if (user.type === "N") missing.push(t`Condizione attuale`);
  if (!user.isNotifMailConfirmed)
    missing.push(t`Mail di Notifica non confermata`);

  if (!user.interests || user.interests.length === 0)
    missing.push(t`Interessi`);
  if (!user.lifestyles || user.lifestyles.length === 0)
    missing.push(t`Abitudini`);
  if (!user.active) missing.push(t`Account non verificato`);
  if (
    user.weekend === -1 ||
    user.alcol === -1 ||
    user.smoke === -1 ||
    user.animals === -1
  )
    missing.push(t`Informazioni Utili`);
  if (user.relationship === -1) missing.push(t`Relazione`);
  if (user.education === -1) missing.push(t`Istruzione`);
  if (!user.videoId || user.videoId.length === 0)
    missing.push(t`Videopresentazione`);
  if (user.description.length < 30)
    missing.push(t`Descrizione (min. 30 caratteri)`);
  if (user.profile_image.includes("default")) missing.push(t`Foto del Profilo`);
  // if (!user.cover_image || user.cover_image.length === 0)
  //   missing.push("Foto di Copertina");

  return missing;
};

function useIsWidthUp(breakpoint: Breakpoint) {
  const theme = useTheme();
  return useMediaQuery(theme.breakpoints.up(breakpoint));
}
function useIsWidthDown(breakpoint: Breakpoint) {
  const theme = useTheme();
  return useMediaQuery(theme.breakpoints.down(breakpoint));
}

export function withWidthHook(
  Component: React.ComponentType<any>,
  breakpoint: Breakpoint
) {
  return function WrappedComponent(props: any) {
    const isUp = useIsWidthUp(breakpoint ?? "sm");
    const isDown = useIsWidthDown(breakpoint ?? "sm");
    return <Component {...props} isUp={isUp} isDown={isDown} />;
  };
}

export function isPartner() {
  return false;
}

export function getPartner() {
  const parts = window.location.hostname.split(".");
  const excludes = ["tcoll", "thipibo", "com", "www", "localhost"];
  const filteredParts = parts.filter((part) => !excludes.includes(part));
  const partner = filteredParts.length > 0 ? filteredParts[0] : null;

  return partner;
}

export async function fetcher({
  baseUrl,
  path,
  method,
  body,
  headers,
  credentials,
  ...rest
}: Omit<RequestInit, "credentials" | "body"> & {
  baseUrl?: string;
  path: string;
  method?: string;
  body?: any;
  headers?: any;
  credentials?: boolean;
}) {
  const partner = getPartner();

  // Tag home/ad mutations with the active property id (multi-home), unless the
  // caller already specified one (e.g. deleteAdvertise sends the viewed home).
  if (HOME_SCOPED_PATHS.has(path) && body && typeof body === "object") {
    if (body.home_id == null) {
      const activeHomeId = getActiveHomeId();
      if (activeHomeId != null) body = { ...body, home_id: activeHomeId };
    }
  }

  const partnerHeader: Record<string, string> = {};
  if (partner) {
    partnerHeader["X-Partner"] = partner.toUpperCase();
  }

  const accessToken = await getAccessToken();
  const r = await fetch(
    (baseUrl ??
      config.app.scheme +
        "://" +
        (partner ? partner + "." : "") +
        config.app.host +
        (config.app.port ? ":" + config.app.port : "")) + path,
    {
      method: method ?? "GET",
      body: body ? JSON.stringify(body) : undefined,
      credentials: credentials ? "include" : undefined,
      headers: {
        "Content-Type": "application/json",
        ...partnerHeader,
        ...headers,
        ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
      },
      ...rest,
    }
  );
  return await r.json();
}
